/**
 * pt-BR is the source of truth. Every key here must exist in `en.ts` — the
 * `satisfies Dictionary` there is what enforces it at compile time.
 *
 * Keys are `area.thing`, flat. Copy stays in the dictionary, never in the
 * component, so a translator never has to read JSX.
 */
export const ptBR = {
  // ── Common ────────────────────────────────────────────────────────────────
  'common.cancel':   'Cancelar',
  'common.save':     'Salvar',
  'common.close':    'Fechar',
  'common.back':     'Voltar',
  'common.retry':    'Tentar novamente',
  'common.confirm':  'Confirmar',
  'common.copy':     'Copiar',
  'common.copied':   'Copiado!',
  'common.loading':  'Carregando…',

  // ── Navigation ────────────────────────────────────────────────────────────
  'nav.home':     'Início',
  'nav.survivor': 'Sobrevivente',
  'nav.leagues':  'Ligas',
  'nav.profile':  'Perfil',
  'nav.openMenu': 'Abrir menu',
  'nav.userMenu': 'Menu do usuário',
  'nav.plans':    'Planos',
  'nav.logout':   'Sair',
  'nav.createGame':   'Criar jogo',
  'nav.joinWithCode': 'Entrar com código',

  // ── Preferences ───────────────────────────────────────────────────────────
  'prefs.title':        'Preferências',
  'prefs.language':     'Idioma',
  'prefs.theme':        'Tema',
  'prefs.theme.system': 'Sistema',
  'prefs.theme.light':  'Claro',
  'prefs.theme.dark':   'Escuro',
  'prefs.openLabel':    'Idioma e tema',
  'prefs.switchToDark':  'Mudar para o tema escuro',
  'prefs.switchToLight': 'Mudar para o tema claro',

  // ── Survivor · status ─────────────────────────────────────────────────────
  'survivor.status.alive':      'Vivo',
  'survivor.status.eliminated': 'Eliminado',
  'survivor.status.champion':   'Campeão',
  'survivor.status.champions':  'Campeões',
  'survivor.status.waiting':    'Aguardando início',
  'survivor.status.inProgress': 'Em andamento',
  'survivor.status.finished':   'Encerrado',

  // ── Survivor · a pick's outcome ───────────────────────────────────────────
  'survivor.pick.won':     'Venceu',
  'survivor.pick.lost':    'Perdeu',
  'survivor.pick.live':    'Em jogo',
  'survivor.pick.pending': 'Aguardando',

  // ── Survivor · the round ──────────────────────────────────────────────────
  'survivor.round':            'Rodada {number}',
  'survivor.round.short':      'Rod. {number}',
  'survivor.round.locked':     'Escolhas bloqueadas',
  'survivor.round.noCalendar': 'Calendário da competição ainda não disponível.',

  // ── Survivor · picking ────────────────────────────────────────────────────
  'survivor.pick.heading':     'Escolha seu time',
  'survivor.pick.yours':       'Sua escolha',
  'survivor.pick.pendingCta':  'Escolha pendente',
  'survivor.pick.choose':      'Escolher time',
  'survivor.pick.missed':      'Você não escolheu esta rodada',
  'survivor.pick.home':        'Casa',
  'survivor.pick.away':        'Fora',
  'survivor.pick.empty':       'Nenhum time disponível nesta rodada.',
  'survivor.pick.othersTitle': 'Escolhas dos outros jogadores',
  'survivor.pick.selected':    'Escolhido',
  'survivor.pick.exhausted':   'Nenhum time disponível — você já usou todos os desta rodada.',
  'survivor.pick.closed':      'As escolhas desta rodada foram fechadas.',
  'survivor.pick.currentOnly': 'Você só pode escolher para a rodada atual.',
  'survivor.pick.gameOver':    'Jogo encerrado.',
  'survivor.pick.eliminatedBanner': 'Você foi eliminado. Acompanhe os demais sobreviventes.',
  'survivor.pick.yoursIs':     'Sua escolha: {team}',
  'survivor.pick.a11y.home':    'Escolher {team} em casa contra {opponent}',
  'survivor.pick.a11y.away':    'Escolher {team} fora de casa contra {opponent}',
  'survivor.pick.a11y.neutral': 'Escolher {team} contra {opponent}',
  'survivor.pick.none':        '— sem escolha',
  'survivor.result.forfeit':     'WO',
  'survivor.result.through':     'Classificado',
  'survivor.result.provisional': 'parcial',
  'survivor.history.title': 'Suas escolhas',
  'survivor.history.empty': 'Você ainda não fez escolhas neste jogo.',
  'survivor.peers.hiddenUntilLock': 'As escolhas dos outros aparecem quando a rodada {number} fechar.',
  'survivor.peers.empty': 'Nenhuma escolha registrada para esta rodada.',

  // ── Survivor · the game ───────────────────────────────────────────────────
  'survivor.game.state':      'Estado',
  'survivor.game.copyCode':   'Copiar código {code}',
  'survivor.game.code':      'Código',
  'survivor.game.aliveCount': 'Vivos',
  'survivor.game.you':       'Você',
  'survivor.game.notFound':  'Jogo não disponível',
  'survivor.game.rules':     'Como funciona o Sobrevivente',
  'survivor.game.invite':    'Convidar participantes',
  'survivor.game.settings':  'Configurações',
  'survivor.game.title':         'Jogo',
  'survivor.game.notifications': 'Notificações',
  'survivor.game.moreOptions':   'Mais opções',
  'survivor.game.editTeamName':  'Editar nome do time',
  'survivor.game.teamNameLocked': 'O nome do time não pode ser alterado após o início do jogo',
  'survivor.game.settingsMenu':  'Configurações do jogo',
  'survivor.game.leave':         'Sair do jogo',
  'survivor.game.leaveConfirm':  'Confirmar saída?',
  'survivor.game.leaveBlocked':  'Transfira a administração antes de sair',

  'survivor.list.error':            'Não foi possível carregar seus jogos. Tente novamente em instantes.',
  'survivor.list.emptyTitle':       'Você ainda não está em nenhum jogo',
  'survivor.list.emptyDescription': 'Crie um jogo novo ou cole o código de convite que você recebeu.',
  'survivor.list.joinCode':         'Código de convite',
  'survivor.list.codeFormat':       'Formato: XXX-XXXX',
  'survivor.list.join':             'Entrar',

  // ── Survivor · standings ──────────────────────────────────────────────────
  'survivor.standings.title':       'Sobreviventes',
  'survivor.standings.meta':        '{alive} vivos · {out} fora',
  'survivor.standings.alive':       'Nenhum jogador vivo.',
  'survivor.standings.noEliminated': 'Ninguém eliminado ainda.',

  // Temporadas / hall da fama
  'survivor.season.short':          'Temporada {number}',
  'survivor.season.restart':        'Nova temporada',
  'survivor.season.restartPrompt':  'O jogo acabou. Recomece com os mesmos participantes — o código de convite continua valendo.',
  'survivor.season.restartConfirm': 'Confirma? Uma nova temporada começa com todos vivos de novo.',
  'survivor.season.restartConfirmCta': 'Confirmar',
  'survivor.season.nextExists':     'A temporada {number} já começou.',
  'survivor.season.goToNext':       'Ir para a temporada atual',
  'survivor.hallOfFame.title':      'Hall da fama',
  'survivor.hallOfFame.inProgress': 'Em disputa',
  'survivor.hallOfFame.noChampion': 'Sem campeão',

  // ── Home ──────────────────────────────────────────────────────────────────
  'greeting.morning':   'Bom dia',
  'greeting.afternoon': 'Boa tarde',
  'greeting.evening':   'Boa noite',

  'home.gamesCount':  { one: '1 jogo',  other: '{count} jogos' },
  'home.activeCount': { one: '1 ativo', other: '{count} ativos' },
  'home.moreGames':   {
    one:   '+1 outro jogo nesta competição',
    other: '+{count} outros jogos nesta competição',
  },
  'home.myGames': 'Meus jogos',

  // ── Fixtures ──────────────────────────────────────────────────────────────
  'phase.groupStage': 'Fase de grupos',
  'phase.r32':        'Trigésimas',
  'phase.r16':        'Oitavas de final',
  'phase.qf':         'Quartas de final',
  'phase.sf':         'Semifinais',
  'phase.final':      'Final',

  'fixture.thisRound': 'Esta rodada',
  'fixture.results':   'Rodada {number} — resultados',
  'fixture.empty':     'Nenhuma partida disponível.',
  'fixture.error':     'Não foi possível carregar as partidas.',
  'fixture.postponed': 'Adiado',
  'fixture.live':      'Ao vivo',
  'fixture.today':     'Hoje',
  'fixture.vs':        'vs',
  'fixture.group':     'Gr.{code}',
  'fixture.count':     { one: '1 partida', other: '{count} partidas' },

  // ── Errors ────────────────────────────────────────────────────────────────
  // Mapped from Common/ErrorCodes.cs. The API also sends a Portuguese `detail`,
  // but rendering it raw leaks server prose into an English UI — see errorMessage.ts.
  'error.generic':                    'Algo deu errado. Tente novamente.',
  'error.network':                    'Sem conexão com o servidor.',
  'error.not_found':                  'Não encontrado.',
  'error.forbidden':                  'Você não tem permissão para isso.',
  'error.validation_failed':          'Confira os dados e tente novamente.',
  'error.internal_error':             'Erro no servidor. Tente novamente em instantes.',
  'error.rate_limit_exceeded':        'Muitas tentativas. Aguarde um pouco.',
  'error.invalid_invite_code':        'Código inválido.',
  'error.invite_code_not_found':      'Código não encontrado.',
  'error.survivor_game_not_found':    'Jogo não encontrado.',
  'error.survivor_already_member':    'Você já está neste jogo.',
  'error.survivor_game_full':         'Este jogo atingiu o limite de participantes.',
  'error.survivor_game_locked':       'Este jogo já começou.',
  'error.survivor_limit_reached':     'Você atingiu o limite de jogos.',
  'error.team_already_picked':        'Você já usou este time.',
  'error.team_not_playing_this_gameweek': 'Este time não joga nesta rodada.',
  'error.team_not_in_competition':    'Este time não está nesta competição.',
  'error.pick_locked':                'A rodada já foi bloqueada.',
  'error.participation_eliminated':   'Você já foi eliminado deste jogo.',
  'error.not_participant':            'Você não participa deste jogo.',
  'error.not_game_owner':             'Só o dono do jogo pode fazer isso.',
  'error.team_name_taken':            'Este nome de time já está em uso neste jogo.',
  'error.no_active_competition':      'Nenhuma competição ativa no momento.',
  'error.survivor.load':              'Não foi possível carregar este jogo.',
  'error.survivor.create':            'Não foi possível criar o jogo.',
  'error.survivor.restart':           'Não foi possível iniciar a nova temporada.',
  'error.survivor_game_not_finished': 'O jogo ainda não terminou.',
  'error.survivor_already_restarted': 'Este jogo já tem uma temporada mais recente.',
  'error.survivor_no_rounds_left':    'A competição não tem mais rodadas para uma nova temporada.',
} as const
